import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { indexToLetter, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import {
    isGoogleReauthRequired,
    refreshIntegrationsOnGoogleReauthRequired,
    toastGoogleBackedError,
} from "./integrationsStore";
import type { TFetchStatus } from "./types";

export const fetchPartnerStatements = createAsyncThunk(
    "settings/getPartnerStatements",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/templates");
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchGoogleSpreadSheets = createAsyncThunk(
    "settings/getGoogleSpreadSheets",
    async (integrationId: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/integrations/google/spreadsheets?integrationId=${integrationId}`,
            );
            return response.data.data;
        } catch (error: any) {
            refreshIntegrationsOnGoogleReauthRequired(error, dispatch);
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchGoogleSheets = createAsyncThunk(
    "settings/getGoogleSheets",
    async (params: { spreadsheetId: string; integrationId: number }, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/integrations/google/spreadsheets/${params.spreadsheetId}?integrationId=${params.integrationId}`,
            );
            return response.data.data;
        } catch (error: any) {
            refreshIntegrationsOnGoogleReauthRequired(error, dispatch);
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchGoogleSheetData = createAsyncThunk(
    "settings/getGoogleSheetData",
    async (params: IFetchSheetParams, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/integrations/google/spreadsheets/${params.spreadsheetId}/sheets/${params.sheetId}?integrationId=${params.integrationId}`,
            );
            const sheetData = response.data.data;

            return sheetData.values.reduce(
                (formatedData: IGoogleSheetData, row: string[], indexRow: number) => {
                    let indexCell;
                    const crealoVariables = row.filter((cell, index) => {
                        indexCell = index;
                        return cell.toString().toLowerCase().includes("crealo:");
                    });
                    if (crealoVariables.length) {
                        crealoVariables.forEach((variable) => (formatedData.varKeys[variable] = null));
                        formatedData.varCount += crealoVariables.length;
                        formatedData.rowsData.push({
                            line: indexRow + 1,
                            variables: crealoVariables,
                            range: `A${indexRow + 1}:${indexToLetter(indexCell)}${indexRow + 1}`,
                        });
                    }
                    return formatedData;
                },
                { rowsData: [], varCount: 0, varKeys: {} },
            );
        } catch (error: any) {
            refreshIntegrationsOnGoogleReauthRequired(error, dispatch);
            return rejectWithValue(error.response.data);
        }
    },
);

export const createStatementTemplate = createAsyncThunk(
    "setting/postStatementTemplate",
    async (requestData: IStatementTemplate, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/statement/templates",
                requestData,
            );
            return response.data.data;
        } catch (error: any) {
            refreshIntegrationsOnGoogleReauthRequired(error, dispatch);
            return rejectWithValue(error.response.data);
        }
    },
);

export const putStatementTemplate = createAsyncThunk(
    "setting/putStatementTemplate",
    async (template: { id: number; reqData: IStatementTemplate }, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/statement/templates/${template.id}`,
                template.reqData,
            );
            return response.data.data;
        } catch (error: any) {
            refreshIntegrationsOnGoogleReauthRequired(error, dispatch);
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteStatementTemplate = createAsyncThunk(
    "setting/deleteStatementTemplate",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/statement/templates/${id}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface IFetchSheetParams {
    spreadsheetId: string;
    sheetId: number;
    integrationId: number;
}

export interface IRowsData {
    line: number;
    variables: string[];
    range: string;
}
export interface IGoogleSheetData {
    rowsData: IRowsData[];
    varCount: number;
    varKeys: Record<string, string | null>;
}

export interface IStatementTemplate {
    id?: number;
    modified?: Date;
    created?: Date;
    name: string;
    integrationId: number;
    type: string;
    data: {
        spreadsheetId: string;
        sheetId: number;
        iterationRange: string;
        variables: Record<string, string>;
    };
}

export interface PartnerStatementsState {
    partnerStatements: IStatementTemplate[];
    googleSpreadSheets: [];
    fetchGoogleSpreadsheetStatus: TFetchStatus;
    collapsedSpreadsheet: { id: string; name: string };
    selectedSheet: { id: number | null; name: string };
    googleSheets: [];
    fetchGoogleSheetsStatus: TFetchStatus;
    googleSheetData: IGoogleSheetData;
    fetchGoogleSheetDataStatus: TFetchStatus;
    fetchCreatePartnerStatementStatus: TFetchStatus;
}

const initialState = {
    partnerStatements: [] as IStatementTemplate[],
    googleSpreadSheets: [],
    fetchGoogleSpreadsheetStatus: "idle",
    collapsedSpreadsheet: { id: "", name: "" },
    selectedSheet: { id: null, name: "" },
    googleSheets: [],
    fetchGoogleSheetsStatus: "idle",
    googleSheetData: { rowsData: [], varCount: 0, varKeys: {} },
    fetchGoogleSheetDataStatus: "idle",
    fetchCreatePartnerStatementStatus: "idle",
};

export const partnerStatementsSlice = createSlice({
    name: "partnerStatements",
    initialState,
    reducers: {
        setGoogleSpreadsheets: (state, action) => {
            state.googleSpreadSheets = action.payload;
        },
        setFetchGoogleSheetStatus: (state, action) => {
            state.fetchGoogleSpreadsheetStatus = action.payload;
        },
        setCollapsedSpreadsheet: (state, action) => {
            state.collapsedSpreadsheet = action.payload;
        },
        setGoogleSheets: (state, action) => {
            state.googleSheets = action.payload;
        },
        setSelectedSheet: (state, action) => {
            state.selectedSheet = action.payload;
        },
        setFetchCreatePartnerStatementStatus: (state, action) => {
            state.fetchCreatePartnerStatementStatus = action.payload;
        },
        resetGoogleSheetData: (state) => {
            state.googleSheetData = initialState.googleSheetData;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchPartnerStatements.fulfilled, (state, action) => {
            state.partnerStatements = action.payload.sort((a: any, b: any) => {
                return new Date(b.modified).valueOf() - new Date(a.modified || a.created).valueOf();
            });
        });
        builder.addCase(fetchPartnerStatements.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchGoogleSpreadSheets.pending, (state) => {
            state.fetchGoogleSpreadsheetStatus = "pending";
        });
        builder.addCase(fetchGoogleSpreadSheets.fulfilled, (state, action) => {
            state.googleSpreadSheets = action.payload;
            state.fetchGoogleSpreadsheetStatus = "succeeded";
        });
        builder.addCase(fetchGoogleSpreadSheets.rejected, (state, { payload }) => {
            state.fetchGoogleSpreadsheetStatus = "failed";
            toastGoogleBackedError(payload);
        });
        builder.addCase(fetchGoogleSheets.pending, (state) => {
            state.fetchGoogleSheetsStatus = "pending";
        });
        builder.addCase(fetchGoogleSheets.fulfilled, (state, action) => {
            state.googleSheets = action.payload;
            state.fetchGoogleSheetsStatus = "succeeded";
        });
        builder.addCase(fetchGoogleSheets.rejected, (state, { payload }) => {
            state.fetchGoogleSheetsStatus = "failed";
            toastGoogleBackedError(payload);
        });
        builder.addCase(fetchGoogleSheetData.pending, (state, action) => {
            state.fetchGoogleSheetDataStatus = "pending";
        });
        builder.addCase(fetchGoogleSheetData.fulfilled, (state, action) => {
            state.googleSheetData = action.payload;
            state.fetchGoogleSheetDataStatus = "succeeded";
        });
        builder.addCase(fetchGoogleSheetData.rejected, (state, { payload }) => {
            state.fetchGoogleSheetDataStatus = "failed";
            state.googleSheetData = initialState.googleSheetData;
            // Other errors stay silent here, as before: only the reconnect message is worth surfacing.
            if (isGoogleReauthRequired(payload)) toastGoogleBackedError(payload);
        });
        builder.addCase(createStatementTemplate.pending, (state, action) => {
            state.fetchCreatePartnerStatementStatus = "pending";
        });
        builder.addCase(createStatementTemplate.fulfilled, (state, action) => {
            state.fetchCreatePartnerStatementStatus = "succeeded";
            state.partnerStatements = [action.payload, ...state.partnerStatements];
            toast.success(`${t("PartnerStatements.CreatedSuccess")}`);
        });
        builder.addCase(createStatementTemplate.rejected, (state, { payload }) => {
            state.fetchCreatePartnerStatementStatus = "failed";
            toastGoogleBackedError(payload);
        });
        builder.addCase(putStatementTemplate.fulfilled, (state, action) => {
            toast.success(`${t("PartnerStatements.EditSuccess")}`);
        });
        builder.addCase(putStatementTemplate.rejected, (state, { payload }) => {
            toastGoogleBackedError(payload);
        });
        builder.addCase(deleteStatementTemplate.fulfilled, (state, action) => {
            toast.success(`${t("PartnerStatements.StatementDeleted")}`);
        });
        builder.addCase(deleteStatementTemplate.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default partnerStatementsSlice.reducer;
export const {
    setGoogleSpreadsheets,
    setFetchGoogleSheetStatus,
    setCollapsedSpreadsheet,
    setGoogleSheets,
    setSelectedSheet,
    setFetchCreatePartnerStatementStatus,
    resetGoogleSheetData,
} = partnerStatementsSlice.actions;
